<template>
  <b-container
    fluid
    class="accordion-container"
  >
    <b-row
      align-v="center"
      align-h="between"
      class="accordion-container__group-title-row"
      @click="collapseHandler(!showGrid)"
    >
      <b-col
        cols="auto"
        v-show="!renaming"
        class="my-2"
      >
        <label class="accordion-container__group-name my-0 text-center">
          {{ group.name }}
          <span v-if="!(group.variables || []).length" class="accordion-container__empty-group">
            ({{ $t('supervision.empty-group-label') }})
          </span>
        </label>
      </b-col>
      <b-col
        cols="6"
        v-show="renaming"
        class="my-2"
      >
        <b-input
          maxlength="80"
          ref="nameInput"
          v-model="newName"
          v-b-tooltip.hover.bottom="$t('actions.enter-the-name-of-the-group')"
          :value="group.name"
          :state="!newNameState"
          :placeholder="$t('actions.enter-the-name-of-the-group')"
          @keyup.esc="quitRenaming"
          @blur="quitRenaming"
          @keyup.enter="renameRequest(newName)"
        />
      </b-col>
      <b-col
        cols="auto"
        align-self="center"
        v-show="!renaming"
        class="text-center"
      >
        <i
          aria-hidden="true"
          class="accordion-container__expand-icon"
          :class="{
            'fas fa-angle-down': !showGrid,
            'fas fa-angle-up': showGrid,
            'accordion-container__disabled': !group.name,
          }"
        />
        <b-dropdown
          no-caret
          right
          size="lg"
          variant="link"
          :disabled="disableDropdown"
          v-if="$hasUserPermission('PER015')"
          v-b-tooltip.hover.bottom="defaultGroupTooltip"
        >
          <template slot="button-content">
            <s-icon
              class="pb-1"
              name="options-menu"
            />
          </template>
          <template
            v-for="({
              text,
              method,
              iconClass,
              textColor,
            }, index) in dropdownItems"
          >
            <b-dropdown-item
              href="#"
              :key="`${text}-${index}`"
              @click="method"
            >
              <i
                :class="iconClass"
                aria-hidden="true"
              />
              <span :class="textColor">
                {{ text }}
              </span>
            </b-dropdown-item>
            <b-dropdown-divider :key="text" v-if="dontRenderLast(index)"/>
          </template>
        </b-dropdown>
      </b-col>
    </b-row>
    <b-collapse
      v-model="showGrid"
      id="accordion-container"
    >
      <b-row
        align-h="center"
        v-show="!group.variables.length"
      >
        <label class="accordion-container__no-variables-label m-3 text-center">
          {{ $t('supervision.empty-group-message') }}
        </label>
      </b-row>
      <draggable
        v-if="$hasUserPermission('PER015')"
        v-bind="dragOptions"
        class="accordion-container__variable-area"
        :disabled="requestingData"
        :list="group.variables"
        @end="finishDragging"
        @change="changeGroup"
        @start="startDragging"
      >
        <template v-for="variable in group.variables">
          <div
            v-show="hasPermissionToViewHiddenVariables(variable)"
            :key="variable.id"
            :class="descriptionTooLongClass(variable)"
          >
            <transition-group
              type="transition"
              :key="variable.id"
              :name="transitionName"
            >
              <variables-card
                :key="variable.id"
                :group-id="group.id"
                :variable="variable"
                :group-list="groupList"
                @reset-variable="resetVariable"
                @toggle-variable-visibility="toggleVisibility"
                @change-variable-group-by-button="changeVariableGroupByButton"
                @rename-variable-request="renameVariableRequest"
                @on-renaming-card="onRenamingCardHandler"
              />
            </transition-group>
          </div>
        </template>
      </draggable>
      <div v-else class="accordion-container__variable-area px-3">
        <template v-for="variable in group.variables">
          <div
            v-if="hasPermissionToViewHiddenVariables(variable)"
            :key="variable.id"
            :class="descriptionTooLongClass(variable)"
          >
            <variables-card
              :key="variable.id"
              :group-id="group.id"
              :variable="variable"
              :group-list="groupList"
              @reset-variable="resetVariable"
              @toggle-variable-visibility="toggleVisibility"
              @change-variable-group-by-button="changeVariableGroupByButton"
              @rename-variable-request="renameVariableRequest"
              @on-renaming-card="onRenamingCardHandler"
            />
          </div>
        </template>
      </div>
    </b-collapse>
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex';
import draggable from 'vuedraggable';
import VariablesCard from './VariablesCard';

export default {
  name: 'GroupAccordion',
  props: {
    groupList: {
      type: Array,
    },
    showHiddenVariables: {
      type: Boolean,
      default: false,
    },
    group: {
      type: Object,
      default: () => ({
        name: null,
        type: null,
        id: null,
        variables: [],
      }),
    },
    requestingData: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    draggable,
    VariablesCard,
  },
  data: () => ({
    list: [],
    newName: null,
    renameHolder: null,
    renaming: false,
    renamingCard: false,
    drag: false,
    showGrid: false,
    statusBeforePrint: false,
  }),
  computed: {
    ...mapGetters([
      'isPrinting',
    ]),
    groupId() {
      return this.group.id;
    },
    transitionName() {
      return !this.drag ? 'accordion-container__flip-list' : null;
    },
    dragOptions() {
      return {
        animation: 200,
        group: 'description',
        disabled: this.renamingCard,
        ghostClass: 'accordion-container__ghost',
      };
    },
    permissionToSupervision() {
      return this.$hasUserPermission('PER001') && this.$hasUserPermission('PER015');
    },
    disableDropdown() {
      return this.group.default
        && this.permissionToSupervision;
    },
    sameNameChecker() {
      return this.groupList.some(({ name }) => name === this.newName)
        ? 'invalid' : null;
    },
    newNameState() {
      if (!this.newName) return 'invalid';

      const sameName = this.sameNameChecker;
      return this.newName === this.group.name ? null : sameName;
    },
    dropdownItems() {
      return [
        {
          text: this.$t('actions.rename-group'),
          iconClass: 'fas fa-pencil-alt mr-2',
          method: this.showRenameInput,
        },
        {
          text: this.$t('actions.delete-group'),
          iconClass: 'far fa-trash-alt mr-2 text-danger',
          textColor: 'text-danger',
          method: this.deleteGroupRequest,
        },
      ];
    },
    defaultGroupTooltip() {
      if (this.group.default) {
        return this.$t('messages.MI101.message');
      }

      return '';
    },
  },
  mounted() {
    this.onHiddenHandler();
  },
  methods: {
    descriptionTooLongClass(variable = {}) {
      const isTooLong = (variable?.name || '').length > 100 || false;

      return isTooLong ? 'accordion-container__variable-name-long' : '';
    },
    onBeforePrint() {
      this.statusBeforePrint = this.showGrid;
      if (this.showGrid) return;
      this.$nextTick(() => {
        this.collapseHandler(true);
      });
    },
    onAfterPrint() {
      if (this.statusBeforePrint) return;
      this.$nextTick(() => {
        this.collapseHandler(false);
      });
    },
    dontRenderLast(index) {
      return this.dropdownItems.length !== index + 1;
    },
    hasPermissionToViewHiddenVariables({ visibility }) {
      return visibility || (this.$hasUserPermission('PER016') && this.showHiddenVariables);
    },
    resetVariable(payload) {
      this.$confirmMessage({ code: 'MC026' })
        .accepted(() => this.$emit('reset-variable', payload));
    },
    toggleVisibility(payload) {
      if (payload.visibility) {
        this.$confirmMessage({ code: 'MC031' })
          .accepted(() => this.$emit('toggle-variable-visibility', payload));
        return;
      }

      this.$emit('toggle-variable-visibility', payload);
    },
    changeVariableGroupByButton(payload) {
      this.$emit('change-variable-group-by-button', payload);
    },
    renameVariableRequest(payload) {
      this.$emit('rename-variable-request', payload);
    },
    collapseHandler(value) {
      this.showGrid = value;
    },
    deleteGroupRequest() {
      this.$confirmMessage({ code: 'MC016' })
        .accepted(() => this.$emit('delete-group-request', this.group.id));
    },
    showRenameInput() {
      this.renaming = true;
      this.newName = this.group.name;
      this.$nextTick(() => this.$refs.nameInput.$el.focus());
    },
    renameRequest(value) {
      if (!this.newName || !value) {
        this.renaming = false;
        return;
      }
      this.renameHolder = true;
      if (this.sameNameChecker === 'invalid') {
        this.$pushAlert({
          code: 'MA020',
        });
        return;
      }
      if (this.newNameState === 'invalid' || !value) return;
      this.renaming = false;
      this.group.name = value;
      this.newName = '';
      const { id, type } = this.group;
      const payload = {
        id,
        type,
        newName: value,
      };
      this.$emit('rename-group-request', payload);
    },
    quitRenaming() {
      if (this.renameHolder) {
        this.renameHolder = false;
        return;
      }

      this.renaming = false;
      this.$emit('quit-renaming');
    },
    startDragging() {
      this.drag = true;
    },
    finishDragging() {
      this.drag = false;
    },
    getOrderWhenActionIsAdded(newIndex) {
      if (newIndex === 0) return 0;
      if (this.group.variables[newIndex + 1] === undefined) return null;
      return this.group.variables[newIndex + 1].order;
    },
    getOrderWhenActionIsMoved(oldIndex, newIndex) {
      const movedForward = newIndex > oldIndex;
      if (movedForward) {
        return this.group.variables[newIndex - 1].order;
      }
      return this.group.variables[newIndex + 1].order;
    },
    changeGroup(payload) {
      const { added, moved } = payload;

      if (added) {
        const { newIndex } = added;
        const order = this.getOrderWhenActionIsAdded(newIndex);
        return this.addHandler(added.element, order);
      }

      if (moved) {
        const { newIndex, oldIndex } = moved;
        const order = this.getOrderWhenActionIsMoved(oldIndex, newIndex);
        return this.sortHandler(moved.element, order);
      }

      return this.removeHandler();
    },
    sortHandler(element, order) {
      const { id: variableId, groupSupervisionFieldId } = element;
      const payload = {
        variableId,
        groupSupervisionFieldId,
        originGroupId: this.group.id,
        destinyGroupId: this.group.id,
        order,
      };
      this.$emit('sorted-variable', payload);
    },
    addHandler(element, order) {
      const { id: variableId, groupSupervisionFieldId } = element;
      const payload = {
        variableId,
        groupSupervisionFieldId,
        originGroupId: null,
        destinyGroupId: this.group.id,
        order,
      };
      this.$emit('added-variable', payload);
    },
    removeHandler() {
      this.$emit('removed-variable', this.group.id);
    },
    handleNewGroup() {
      if (this.group.name) {
        return;
      }

      this.showRenameInput();
    },
    onHiddenHandler() {
      this.$emit('on-hidden-handler');
    },
    onRenamingCardHandler(state) {
      this.renamingCard = state;
    },
  },
  created() {
    this.handleNewGroup();
  },
  watch: {
    isPrinting(newValue, oldValue) {
      if (newValue && !oldValue) {
        this.onBeforePrint();
      }
      if (!newValue && oldValue) {
        this.onAfterPrint();
      }
    },
  },
};

</script>

<style lang="sass" scoped>
.accordion-container
  border: 1px solid var(--color-gray-medium-light)
  border-radius: 0.25rem

  &__group-title-row
    &:hover
      cursor: pointer

  &__disabled
    pointer-events: none

  &__no-variables-label
    font-size: 1.1em

  &__expand-icon
    cursor: pointer

  &__group-name
    color: var(--color-primary)

  &__empty-group
    color: var(--color-neutral-dark) !important
    font-style: italic

  &__variable-area
    display: grid
    grid-template-columns: repeat(auto-fill, minmax(215px, 1fr))
    grid-gap: 15px
    margin-bottom: 15px

  &__ghost
    opacity: 0.5
    border-style: dotted

  &__flip-list
    transition: transform 0.5s

@media print
  .accordion-container
    &__variable-area
      display: block

      & > div
        display: inline-block
        margin: 10px
        vertical-align: bottom
        &:not(.accordion-container__variable-name-long)
          height: 215px
          width: calc(25% - 20px)
          order: 2

  .accordion-container__variable-name-long
    min-height: 215px
    min-width: calc(25% - 20px)
    order: 1

  ::v-deep .reader-card__value-result
    font-size: 1.5rem !important

</style>
